import React, { useCallback } from "react";
import { MenuItem } from "@shared/interfaces";
import { Feature, RoleName, User } from "@shared/models";
import { TruncatedTextTooltip } from "@shared/components";
import { getFullName } from "@shared/utils";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { setActiveAccount } from "@containers/Account/store/actions";
import { getActiveAccount } from "@containers/Account/store/selectors";
import { useCheckRoles, useLocalStorage } from "@shared/hooks";
import { ImpersonationInfo } from "@shared/components/Sidebar/components/ImpersonationInfo/ImpersonationInfo";
import { CustomerManager } from "@shared/components/Sidebar/components/CustomerManager/CustomerManager";
import CompanyItem from "@shared/components/Sidebar/components/MainSidebar/CompanyItem";
import { EXPANDED_USER_ITEMS } from "@shared/constants/sidebar";
import { ReactComponent as LogoIcon } from "@assets/files/icons/general/Logo-DF+RS.svg";
import { ReactComponent as ToggleIcon } from "@assets/files/icons/sidebar/redesign/toggle.svg";
import { ReactComponent as UserIcon } from "@assets/files/icons/sidebar/redesign/user.svg";
import { ReactComponent as ChevronIcon } from "@assets/files/icons/sidebar/redesign/chevron.svg";
import { useFetchReiSiftIntegrationSummary } from "@shared/components/Integrations/useFetchReiSiftIntegrationSummary";

import { SidebarItems } from "../SidebarItems";
import config from "../../../../../config";

import "./index.scss";

export interface MainSidebarProps {
  user: User | null;
  items: MenuItem[];
  onLogoutClick: () => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const Sidebar = (props: MainSidebarProps) => {
  const { user, items, collapsed, setCollapsed, onLogoutClick } = props;
  const accounts = user?.accounts;

  const dispatch = useDispatch();
  const activeAccount = useSelector(getActiveAccount());

  const [isExpanded, setIsExpanded] = useLocalStorage("@sidebar/account/expanded", false);

  const classes = classNames("main-sidebar", { collapsed });
  const sidebarItemsClasses = classNames("sidebar-items", collapsed ? "collapsed" : "");

  const isCompanySwitcherEnabled = !useCheckRoles([RoleName.admin, RoleName.super_admin]);

  const handleExpandLogoutSection = () => setIsExpanded((v) => !v);

  const handleSelectAccount = useCallback((id: number) => dispatch(setActiveAccount.request(id)), [dispatch]);

  const { data: integrationSummary } = useFetchReiSiftIntegrationSummary();

  const menuItems = items.map((item) => {
    if (item.feature === Feature.ReiSift && integrationSummary?.status === true) {
      return {
        ...item,
        onClick: () => window.open(config.reiSift.appUrl, "_blank"),
      };
    }
    return item;
  });

  return (
    <div className={classNames("main-sidebar-wrapper", { collapsed })}>
      <div className={classes}>
        <div className="sidebar-header">
          <div className="sidebar-logo">{collapsed ? <LogoIcon width="80" height="38" /> : <LogoIcon />}</div>
          <div className="collapse-button" onClick={() => setCollapsed(!collapsed)} role="button">
            <ToggleIcon />
          </div>
        </div>
        <div className={`sidebar-account-panel ${isExpanded ? "expanded" : ""}`}>
          <div className="sidebar-account-panel-item" onClick={handleExpandLogoutSection}>
            <UserIcon />
            {!collapsed && (
              <div className="user-name">
                <TruncatedTextTooltip text={getFullName(user)} limit={15} position="bottom-right" />
              </div>
            )}
            {!collapsed && (
              <div className={classNames("sidebar-account-panel-chevron", isExpanded ? "expand-icon" : "narrow-icon")}>
                <ChevronIcon />
              </div>
            )}
          </div>

          {isCompanySwitcherEnabled && !collapsed && isExpanded && (
            <div className="company-wrapper expanded">
              <div className="header">Company</div>
              {accounts?.map((account) => (
                <CompanyItem
                  key={account.id}
                  account={account}
                  isActive={account.id === activeAccount?.id}
                  handleSelectAccount={() => handleSelectAccount(account.id)}
                />
              ))}
            </div>
          )}

          {isExpanded && (
            <>
              <div className={sidebarItemsClasses}>
                <SidebarItems
                  setCollapsed={setCollapsed}
                  collapsed={collapsed}
                  items={EXPANDED_USER_ITEMS(onLogoutClick)}
                  className={collapsed ? "collapsed" : ""}
                />
              </div>
            </>
          )}
        </div>

        <CustomerManager />

        <div className={sidebarItemsClasses}>
          <SidebarItems
            setCollapsed={setCollapsed}
            collapsed={collapsed}
            items={menuItems}
            className={collapsed ? "collapsed" : ""}
          />
        </div>
        <ImpersonationInfo />
      </div>
    </div>
  );
};

export default Sidebar;
